import ActionTextIcon from "@/components/icons/ActionTextIcon";
import StatusTag from "@/components/tags/StatusTag";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleDate, formatLocalePercentageNumber } from "@/utils/formatUtils";
import { useUpdateFilterSearchParams } from "@/utils/hooksUtils";
import { getTableSorting, sortableColumnProps } from "@/utils/tableUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import { Col, Collapse, Divider, Row, Space, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import classNames from "classnames";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";
import { useDownloadSplitCommissionsExport, useFilterSplitCommissions } from "../../../../../common/api/queries";
import { rowGutter, TableSizes } from "../../../../../common/constants";
import { ExportFileType } from "../../../../../common/enums";
import { Permission } from "../../../../../common/security/enums";
import ComponentWithPermission from "../../../../auth/components/ComponentWithPermission";
import { useHasAnyPermission } from "../../../../auth/hooks";
import { CONTRACT_ROUTE_PATHS } from "../../../../contract/paths";
import { CommissionKindTag } from "../../../batch/components/CommissionKindTag";
import { CalculatedCommissionsModalForm } from "../../../batch/components/forms/CalculatedCommissionsModalForm";
import { COMMISSIONS_BATCH_ROUTE_PATHS } from "../../../batch/paths";
import { getCalculatedCommissionGroup } from "../../../batch/utils";
import { CalculatedCommissionsGroupList } from "../../../components/CalculatedCommissionsGroupList";
import { DEFAULT_SPLIT_COMMISSIONS_QUERY_PARAMS } from "../../api";
import { SplitCommissionsOrderByColumn, SplitCommissionsReport } from "../../enums";
import { SplitCommission, SplitCommissionsFilterPageRequest } from "../../types";
import { UpdateSplitCommissionModalForm } from "../forms/UpdateSplitCommissionModalForm";

type Props = {
  searchParams: SplitCommissionsFilterPageRequest;
};

export const SplitCommissionsTable = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const updateSearchParams = useUpdateFilterSearchParams<SplitCommissionsFilterPageRequest>(
    DEFAULT_SPLIT_COMMISSIONS_QUERY_PARAMS,
  );

  const [updateSplitCommission, setUpdateSplitCommission] = useState<SplitCommission>();
  const [commissionToUpdate, setCommissionToUpdate] = useState<SplitCommission & { finalCommissionAmount?: number }>();

  const { data, isFetching } = useFilterSplitCommissions(searchParams);
  const downloadSplitCommissionsExport = useDownloadSplitCommissionsExport();

  const hasReadContractPermission = useHasAnyPermission(
    Permission.INSURANCE_READ,
    Permission.LOAN_READ,
    Permission.INVESTMENT_READ,
    Permission.DEPOSIT_READ,
    Permission.SECOND_PILLAR_READ,
    Permission.THIRD_PILLAR_READ,
    Permission.GENERIC_READ,
  );

  const columns: ColumnsType<SplitCommission> = useMemo(
    () => [
      {
        key: "originalBatch",
        ...sortableColumnProps(SplitCommissionsOrderByColumn.ORIGINAL_BATCH_NAME, data),
        title: t("commissions.splitCommissions.helpers.originalBatch"),
        width: 160,
        ellipsis: { showTitle: false },
        render: (_, record) => (
          <Ellipsis tooltip={record.originalCommission.batch.name}>
            <Link
              to={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.detail.to, { id: record.originalCommission.batch.id })}
              target="_blank"
            >
              {record.originalCommission.batch.name}
            </Link>
          </Ellipsis>
        ),
      },
      {
        key: "payoutBatch",
        ...sortableColumnProps(SplitCommissionsOrderByColumn.PAYMENT_PERIOD, data),
        title: t("commissions.splitCommissions.helpers.payoutBatch"),
        width: 170,
        ellipsis: { showTitle: false },
        render: (_, record) =>
          record.payoutCommission ? (
            <Ellipsis tooltip={record.payoutCommission.batch.name}>
              <Link
                to={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.detail.to, { id: record.payoutCommission.batch.id })}
                target="_blank"
              >
                {record.payoutCommission.batch.name}
              </Link>
            </Ellipsis>
          ) : (
            <Ellipsis>{record.paymentPeriod}</Ellipsis>
          ),
      },
      {
        key: "dataRange",
        title: t("commissions.splitCommissions.helpers.dataRangeLabel"),
        width: 150,
        ellipsis: { showTitle: false },
        render: (_, record) => (
          <Ellipsis>{`${t("common.year")} ${record.paymentYear} (${formatLocalePercentageNumber(record.paymentRatio)})`}</Ellipsis>
        ),
      },
      {
        key: "institution",
        title: t("commissions.batch.attrs.commission.institution"),
        width: 140,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.originalCommission.institution.name}</Ellipsis>,
      },
      {
        key: "contract",
        title: t("commissions.batch.attrs.commission.contract"),
        width: 130,
        ellipsis: { showTitle: false },
        render: (_, record) =>
          record.originalCommission.contract && hasReadContractPermission ? (
            <>
              {record.originalCommission.contractNumber && (
                <>
                  <Ellipsis tooltip={record.originalCommission.contractNumber}>
                    <Link
                      to={generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: record.originalCommission.contract.id })}
                      target="_blank"
                    >
                      {record.originalCommission.contractNumber}
                    </Link>
                  </Ellipsis>
                  <br />
                </>
              )}

              {record.originalCommission.secondaryContractNumber && (
                <Ellipsis tooltip={record.originalCommission.secondaryContractNumber}>
                  <Link
                    to={generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: record.originalCommission.contract.id })}
                    target="_blank"
                  >
                    {record.originalCommission.secondaryContractNumber}
                  </Link>
                </Ellipsis>
              )}
            </>
          ) : (
            <>
              <div>
                <Ellipsis>{record.originalCommission.contractNumber}</Ellipsis>
              </div>
              <div>
                <Ellipsis>{record.originalCommission.secondaryContractNumber}</Ellipsis>
              </div>
            </>
          ),
      },
      {
        key: "product",
        title: t("commissions.batch.attrs.commission.product"),
        width: 150,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.originalCommission.contract?.product.name}</Ellipsis>,
      },
      {
        key: "tariffCode",
        title: t("commissions.batch.attrs.commission.tariffCode"),
        width: 90,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.originalCommission.tariffCode}</Ellipsis>,
      },
      {
        key: "clientName",
        title: t("commissions.batch.attrs.commission.clientName"),
        width: 160,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.originalCommission.clientName}</Ellipsis>,
      },
      {
        key: "dates",
        title: t("commissions.batch.attrs.commission.dates"),
        width: 100,
        render: (_, record) => (
          <>
            {record.originalCommission.startDate ? formatLocaleDate(record.originalCommission.startDate) : undefined}
            <br />
            {record.originalCommission.endDate ? formatLocaleDate(record.originalCommission.endDate) : undefined}
          </>
        ),
      },
      {
        key: "commissionKind",
        title: t("commissions.batch.enums.commissionKind._label"),
        align: "center",
        width: 205,
        render: (_, record) =>
          record.originalCommission.commissionKind ? (
            <CommissionKindTag kind={record.originalCommission.commissionKind} />
          ) : undefined,
      },
      {
        key: "commissionBaseAmount",
        ...sortableColumnProps(SplitCommissionsOrderByColumn.ORIGINAL_COMMISSION_BASE_AMOUNT, data),
        title: t("commissions.batch.attrs.commission.commissionBaseAmount"),
        align: "right",
        width: 145,
        ellipsis: { showTitle: false },
        render: (_, record) => (
          <Ellipsis>
            {isDefined(record.originalCommission.commissionBaseAmount)
              ? formatLocaleCurrency(record.originalCommission.commissionBaseAmount)
              : undefined}
          </Ellipsis>
        ),
      },
      {
        key: "commissionAmount",
        ...sortableColumnProps(SplitCommissionsOrderByColumn.COMMISSION_AMOUNT, data),
        title: t("commissions.batch.attrs.commission.commissionAmount"),
        align: "right",
        width: 130,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{formatLocaleCurrency(record.commissionAmount)}</Ellipsis>,
      },
      {
        key: "errorTag",
        align: "center",
        width: 100,
        render: (_, record) =>
          !!record.errors.length && (
            <StatusTag status="error" tooltip={t("commissions.batch.helpers.calculationError")} />
          ),
      },
      {
        key: "actions",
        align: "right",
        width: 90,
        render: (_, record) =>
          data?.report === SplitCommissionsReport.PLANNED_IN_FINISHED_BATCH ? (
            <ComponentWithPermission permissions={[Permission.COMMISSIONS_MANAGE]}>
              <ActionTextIcon
                icon="edit"
                color="blue"
                text={t("common.edit")}
                onClick={() => setUpdateSplitCommission(record)}
              />
            </ComponentWithPermission>
          ) : undefined,
      },
    ],
    [t, data, hasReadContractPermission],
  );

  return (
    <>
      {data?.totalElementsCount ? (
        <Space size="large">
          <Space>
            <b>{t("commissions.batch.helpers.filteredCommissionsSum")}:</b>
            <span>{formatLocaleCurrency(data?.commissionAmountsSum ?? 0)}</span>
          </Space>

          <div>
            <ActionTextIcon
              icon="download"
              color="blue"
              text={t("common.exportXlsx")}
              onClick={() =>
                downloadSplitCommissionsExport.mutate({ ...searchParams, exportFileType: ExportFileType.XLSX })
              }
            />

            <Divider orientation="vertical" />

            <ActionTextIcon
              icon="download"
              color="green"
              text={t("common.exportCsv")}
              onClick={() =>
                downloadSplitCommissionsExport.mutate({ ...searchParams, exportFileType: ExportFileType.CSV })
              }
            />
          </div>
        </Space>
      ) : undefined}

      <Table<SplitCommission>
        {...tableStandardProps()}
        loading={isFetching}
        columns={columns}
        dataSource={data?.pageData}
        scroll={{ x: TableSizes.XXL }}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
        }}
        onChange={(pagination, __, sorter) => {
          const { orderBy, orderDirections } = getTableSorting<SplitCommissionsOrderByColumn>(sorter);

          updateSearchParams(
            { orderBy, orderDirections, pageIndex: pagination.current ? pagination.current - 1 : undefined },
            true,
          );
        }}
        expandable={{
          rowExpandable: () => true,
          columnWidth: 30,
          expandedRowRender: (record) => (
            <>
              {!!record.errors.length && (
                <Row gutter={rowGutter} className={classNames("margin-top-medium", "margin-bottom-medium")}>
                  <Col flex="70px">
                    <b>{t("common.errors")}:</b>
                  </Col>
                  <Col flex="1 1 0">{record.errors.map((error) => error.description).join(", ")}</Col>
                </Row>
              )}

              <Collapse
                defaultActiveKey={[0]}
                items={[
                  {
                    key: 0,
                    label: t("commissions.batch.helpers.calculateCommission"),
                    children: (
                      <CalculatedCommissionsGroupList
                        calculatedCommissionsGroup={getCalculatedCommissionGroup(record)}
                        onUpdate={
                          data?.report === SplitCommissionsReport.PLANNED_IN_FINISHED_BATCH
                            ? () => setCommissionToUpdate({ ...record, finalCommissionAmount: record.commissionAmount })
                            : undefined
                        }
                      />
                    ),
                  },
                ]}
              />
            </>
          ),
        }}
      />

      <CalculatedCommissionsModalForm
        open={!!commissionToUpdate}
        itemId={commissionToUpdate?.id ?? ""}
        type="splitCalculated"
        calculatedCommissions={commissionToUpdate?.calculatedCommissions ?? []}
        finalCommissionAmount={commissionToUpdate?.finalCommissionAmount}
        onFormCancel={() => setCommissionToUpdate(undefined)}
      />

      <UpdateSplitCommissionModalForm
        isOpen={!!updateSplitCommission}
        commission={updateSplitCommission}
        onCancel={() => setUpdateSplitCommission(undefined)}
      />
    </>
  );
};
